import { Spinner } from "@/ui/atoms/Spinner";
import { formatDate } from "@/utils";
import { CheckCircleIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Link } from "react-router-dom";

type TreeNodeRowProps = {
    icon: "check" | "ring" | "plus" | "spinner" | "none";
    label: string;
    id?: string;
    date?: string;
    to?: string;
    onClick?: () => void;
    isSelected?: boolean;
    lineElement?: React.ReactNode;
    subdued?: boolean;
    bold?: boolean;
};

export function FileNode({
    icon,
    label,
    id,
    date,
    to,
    onClick,
    isSelected = false,
    lineElement,
    subdued = false,
    bold = false,
}: TreeNodeRowProps) {
    const content = (
        <div
            className={clsx(
                "flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1",
                to && "hover:bg-hover",
                isSelected && "bg-selected",
            )}
        >
            <strong
                className={clsx(
                    "body-s cursor-pointer",
                    bold && "font-semibold",
                    subdued &&
                        "font-normal text-slate-400 group-hover:text-white",
                )}
            >
                {label}
            </strong>
            {id && (
                <p className={clsx("body-s cursor-pointer text-slate-500")}>
                    {id.slice(-6)}
                </p>
            )}
        </div>
    );

    return (
        <div
            className={clsx(
                "group relative flex items-center justify-between gap-4",
            )}
        >
            {lineElement}
            <div className={clsx("flex items-center gap-3")}>
                <div className="relative z-10">
                    <div
                        className={clsx(
                            "flex size-4 items-center justify-center",
                        )}
                    >
                        {icon === "check" && (
                            <CheckCircleIcon className="size-4" />
                        )}
                        {icon === "ring" && (
                            <div
                                className={clsx(
                                    "size-2.5 rounded-full ring-1 ring-slate-700",
                                )}
                            />
                        )}
                        {icon === "plus" && (
                            <PlusCircleIcon
                                className={clsx(
                                    "size-4 stroke-slate-400 group-hover:stroke-white",
                                )}
                            />
                        )}
                        {icon === "spinner" && (
                            <Spinner className="size-4 text-slate-400" />
                        )}
                    </div>
                </div>
                {to ? (
                    <Link to={to} className="cursor-pointer">
                        {content}
                    </Link>
                ) : onClick ? (
                    <button
                        onClick={onClick}
                        className="no-styles cursor-pointer"
                    >
                        {content}
                    </button>
                ) : (
                    content
                )}
            </div>
            {date && (
                <p className={clsx("body-micro mt-px text-gray-500")}>
                    {formatDate(date, { outputFormat: "MM/DD/YY" })}
                </p>
            )}
        </div>
    );
}
